/**
 * P-13 激活设备（docs/03-UIUX.md §5）。
 *
 * 跨授权设备列表，展示设备指纹、状态、最近心跳。
 * 支持管理员强制解绑（T1）：reason 必填，写入审计；解绑后客户端下次校验即失效。
 */
import { useCallback, useState } from 'react';
import { ErrorBlock, EmptyFirstRun } from '../components/DataState';
import { Pager, DEFAULT_PAGE_SIZE, type PageSize } from '../components/Pager';
import { describeError } from '../api/client';
import { deactivateActivation, listActivations, type Activation } from '../api/endpoints';
import { useAsync } from '../lib/useAsync';
import { formatDateTime, formatDate, relativeTime } from '../lib/format';
import { Icon } from '../icons/Icon';
import { ICON_SIZE } from '../tokens';

/** 心跳超时警示阈值：超过 3 天未心跳的设备视为疑似失联（P-13）。 */
const HEARTBEAT_STALE_SECONDS = 3 * 24 * 60 * 60;

const STATUS_LABEL: Record<string, string> = {
  active: '活跃',
  deactivated: '已解绑',
};

/** 心跳列：超过阈值显示 clock-alert + 相对时间，否则绝对时间。 */
function HeartbeatCell({ lastSeenAt }: { lastSeenAt: number | null }) {
  if (lastSeenAt === null) return <span className="text-muted">-</span>;
  const stale = Math.floor(Date.now() / 1000) - lastSeenAt > HEARTBEAT_STALE_SECONDS;
  if (!stale) return <span>{formatDateTime(lastSeenAt)}</span>;
  return (
    <span className="cell-stale" title={`超过 ${Math.floor(HEARTBEAT_STALE_SECONDS / 86400)} 天未心跳，疑似失联`}>
      <Icon name="clock-alert" size={ICON_SIZE.inline} color="var(--st-danger)" />
      {relativeTime(lastSeenAt).text}
    </span>
  );
}

export function ActivationsPage() {
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState<PageSize>(DEFAULT_PAGE_SIZE);
  const [deactivateTarget, setDeactivateTarget] = useState<Activation | null>(null);

  const activations = useAsync(
    useCallback(() => listActivations({ page, limit }), [page, limit]),
    [page, limit],
  );

  const list: readonly Activation[] = activations.state.kind === 'ready' ? activations.state.data.items : [];
  const total = activations.state.kind === 'ready' ? activations.state.data.total : 0;
  const hasMore = activations.state.kind === 'ready' ? activations.state.data.hasMore : false;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-head__title">激活设备</h1>
          <p className="page-head__sub">跨授权设备列表。强制解绑后，该设备下次校验即失效（离线最迟在宽限期后失效）。</p>
        </div>
      </div>

      {activations.state.kind === 'failed' ? (
        <div className="table-frame">
          <ErrorBlock message={describeError(activations.state.error, '加载激活设备')} onRetry={activations.reload} />
        </div>
      ) : activations.state.kind === 'loading' ? (
        <div className="table-frame">
          <table className="table"><ActivationSkeleton rows={8} /></table>
        </div>
      ) : list.length === 0 ? (
        <div className="table-frame">
          <EmptyFirstRun icon="monitor-smartphone" description="暂无激活设备。客户端兑换授权码后会自动激活。" />
        </div>
      ) : (
        <>
          <div className="table-frame">
            <table className="table">
              <thead>
                <tr>
                  <th>设备名称</th>
                  <th>设备指纹</th>
                  <th>状态</th>
                  <th>激活时间</th>
                  <th>最近心跳</th>
                  <th className="table__actions-col">操作</th>
                </tr>
              </thead>
              <tbody>
                {list.map((a) => (
                  <tr key={a.id}>
                    <td>{a.device_name ?? '-'}</td>
                    <td><code style={{ fontSize: 11 }}>{a.device_hash.slice(0, 16)}…</code></td>
                    <td>
                      <span className={`status-dot status-dot--${a.status === 'active' ? 'active' : 'archived'}`} />
                      {STATUS_LABEL[a.status] ?? a.status}
                    </td>
                    <td>{formatDate(a.activated_at)}</td>
                    <td>{a.last_seen_at ? <HeartbeatCell lastSeenAt={a.last_seen_at} /> : '-'}</td>
                    <td className="table__actions-col">
                      {a.status === 'active' ? (
                        <button
                          type="button"
                          className="btn btn--quiet btn--icon"
                          aria-label="强制解绑该设备"
                          title="强制解绑"
                          onClick={() => setDeactivateTarget(a)}
                        >
                          <Icon name="unplug" size={ICON_SIZE.inline} />
                        </button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pager page={page} limit={limit} total={total} hasMore={hasMore} onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} />
        </>
      )}

      {deactivateTarget !== null && (
        <DeactivateDialog
          activation={deactivateTarget}
          onClose={() => setDeactivateTarget(null)}
          onDeactivated={() => {
            setDeactivateTarget(null);
            activations.reload();
          }}
        />
      )}
    </>
  );
}

function DeactivateDialog({
  activation,
  onClose,
  onDeactivated,
}: {
  activation: Activation;
  onClose: () => void;
  onDeactivated: () => void;
}) {
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handle = async () => {
    if (!reason.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await deactivateActivation(activation.id, reason.trim());
      onDeactivated();
    } catch (e) {
      setError(e instanceof Error ? e.message : '解绑失败');
      setSubmitting(false);
    }
  };

  return (
    <div className="panel-overlay" onClick={onClose}>
      <div className="panel" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label="解绑设备">
        <div className="panel__head">
          <h2 className="panel__title">解绑设备</h2>
          <button type="button" className="btn btn--quiet" onClick={onClose} aria-label="关闭">
            <Icon name="x" size={ICON_SIZE.control} />
          </button>
        </div>
        <div className="panel__body">
          <p className="text-muted">
            将解绑设备 <code>{activation.device_hash.slice(0, 16)}…</code>。解绑原因会写入审计日志，客户端下次校验即失效。
          </p>
          <div className="field">
            <label htmlFor="deactivate-reason">解绑原因（必填）</label>
            <input
              id="deactivate-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="如：设备丢失 / 疑似盗用 / 换机"
            />
          </div>
          {error && <p className="login-error"><Icon name="octagon-x" size={ICON_SIZE.inline} />{error}</p>}
          <div className="dialog-actions">
            <button type="button" className="btn" onClick={onClose} disabled={submitting}>取消</button>
            <button type="button" className="btn btn--danger" disabled={submitting || !reason.trim()} onClick={handle}>
              {submitting ? '解绑中…' : '确认解绑'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActivationSkeleton({ rows }: { rows: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, i) => (
        <tr key={i}>
          {[40, 55, 30, 45, 45, 20].map((w, j) => (
            <td key={j}><div className="skeleton-bar" style={{ width: `${w}%` }} /></td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}
